import debounce from 'debounce'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTheme } from 'styled-components'

import { useWindowSize } from '@nx/hooks/use-window-size'
import { getTranslate } from '@nx/translations'

import {
  ExpandableArea,
  FadeOut,
  ReadMoreButton,
} from './FireExpandableContent.styles'
import translate from './FireExpandableContent.translate.json'

const { text } = getTranslate(translate)

export function FireExpandableContent({
  cutOffHeight,
  defaultIsExpanded = false,
  fadeOutColour,
  children,
}: ExpandableContentProps) {
  const [isExpanded, setIsExpanded] = useState(defaultIsExpanded)
  const [contentHeight, setContentHeight] = useState(0)
  const [contentIsLarger, setContentIsLarger] = useState(false)
  const contentInnerRef = useRef<HTMLDivElement>(null)
  const { breakpoints } = useTheme()
  const { width: siteWidth } = useWindowSize()

  const getCutOffHeight = useCallback((): number => {
    if (!siteWidth || (siteWidth > 0 && siteWidth < breakpoints.sm)) {
      return cutOffHeight.xs
    } else if (siteWidth > breakpoints.sm && siteWidth < breakpoints.md) {
      return cutOffHeight.sm
    } else if (siteWidth > breakpoints.md && siteWidth < breakpoints.lg) {
      return cutOffHeight.md
    } else if (siteWidth > breakpoints.lg && siteWidth < breakpoints.xl) {
      return cutOffHeight.lg
    } else if (siteWidth > breakpoints.xl && siteWidth < breakpoints.xxl) {
      return cutOffHeight.xl
    } else {
      return cutOffHeight.xxl
    }
  }, [siteWidth, breakpoints, cutOffHeight])

  useEffect(() => {
    setIsExpanded(defaultIsExpanded)
  }, [defaultIsExpanded])

  const detectContentHeight = debounce(
    useCallback(() => {
      if (contentInnerRef.current) {
        const contentHeight = contentInnerRef.current.offsetHeight + 15

        setContentHeight(contentHeight)
        setContentIsLarger(contentHeight > getCutOffHeight())
      }
    }, [
      contentInnerRef,
      getCutOffHeight,
      setContentHeight,
      setContentIsLarger,
    ]),
    100
  )

  useEffect(() => {
    setIsExpanded(false)
    setContentHeight(0)
    setContentIsLarger(false)
  }, [children])

  useEffect(() => {
    detectContentHeight()
    window.addEventListener('resize', detectContentHeight)

    return () => window.removeEventListener('resize', detectContentHeight)
  }, [detectContentHeight, children])

  return (
    <div>
      <ExpandableArea
        $isExpanded={isExpanded}
        $cutOffHeight={getCutOffHeight()}
        $contentHeight={contentHeight}
        $contentIsLarger={contentIsLarger}
        data-testid="expandable_area"
      >
        <>
          <div ref={contentInnerRef}>{children}</div>
          <FadeOut
            $fadeOutColour={fadeOutColour}
            $showFade={contentIsLarger && !isExpanded}
            data-testid="expandable_area_fade"
          />
        </>
      </ExpandableArea>
      {contentIsLarger && (
        <ReadMoreButton
          as="button"
          onClick={() => setIsExpanded((prevState) => !prevState)}
        >
          {isExpanded ? text('less') : text('more')}
        </ReadMoreButton>
      )}
    </div>
  )
}

interface ExpandableContentProps {
  cutOffHeight: {
    xs: number
    sm: number
    md: number
    lg: number
    xl: number
    xxl: number
  }
  defaultIsExpanded?: boolean
  fadeOutColour?: string
  children: React.ReactNode
}
